'use client';
import React from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

export interface Campaign {
  id: string;
  brand: string;
  product: string;
  category: string;
  image_url: string;
  reward_amount: number;
  avg_rating: number;
  available_slots: number;
  marketplace_name: string;
  filled_slots: number;
  total_slots: number;
}

interface CampaignCardProps {
  campaign: Campaign;
  onApplyClick: (campaign: {
    id: string;
    brand: string;
    product: string;
    category: string;
    reward_amount: number;
  }) => void;
}

export default function CampaignCard({ campaign, onApplyClick }: CampaignCardProps) {
  return (
    <div className="bg-card rounded-2xl border border-border overflow-hidden card-hover">
      <div className="img-zoom h-44 relative">
        <AppImage
          src={campaign.image_url || '/assets/images/no_image.png'}
          alt={`${campaign.product} — review campaign product image`}
          fill
          className="object-cover"
          sizes="(max-width: 640px) 100vw, (max-width: 1280px) 50vw, 33vw"
        />
        <div className="absolute top-3 right-3">
          <span className="text-[10px] font-semibold bg-white/90 text-foreground px-2 py-0.5 rounded-full">
            {campaign.marketplace_name}
          </span>
        </div>
      </div>
      <div className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-primary bg-primary-container px-2 py-0.5 rounded-full">
            {campaign.category}
          </span>
          <div className="flex items-center gap-1">
            <Icon name="StarIcon" size={12} variant="solid" className="star-filled" />
            <span className="text-xs text-muted-foreground">
              {(campaign.avg_rating || 0).toFixed(1)} ({campaign.filled_slots || 0})
            </span>
          </div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">{campaign.brand}</div>
          <h3 className="font-heading font-semibold text-foreground line-clamp-2 mt-0.5 text-sm">
            {campaign.product}
          </h3>
        </div>
        <div className="flex items-center justify-between">
          <span className="font-heading font-bold text-primary text-lg">
            ₹{(campaign.reward_amount || 0).toLocaleString('en-IN')}
          </span>
          <span
            className={`text-xs font-medium ${
              (campaign.available_slots || 0) <= 5 ? 'text-red-500' : 'text-muted-foreground'
            }`}
          >
            {campaign.available_slots || 0} slots left
          </span>
        </div>
        <div className="flex gap-2">
          <Link
            href={`/product-detail?id=${campaign.id}`}
            className="flex-1 text-center text-xs font-semibold text-primary border border-primary rounded-xl py-2 hover:bg-primary-container transition-colors"
          >
            View Details
          </Link>
          <button
            onClick={() => onApplyClick({
              id: campaign.id,
              brand: campaign.brand,
              product: campaign.product,
              category: campaign.category,
              reward_amount: campaign.reward_amount,
            })}
            className="flex-1 cta-gradient text-white text-xs font-semibold rounded-xl py-2 hover:opacity-90 transition-opacity"
          >
            Apply Now
          </button>
        </div>
      </div>
    </div>
  );
}
